import { useCallback, useEffect, useState } from 'react';
import { Button } from '@shopify/polaris';

/**
 * Collapsible settings sections: closed until opened, and opened by themselves when they hold
 * something that needs attention, so a problem is never hidden behind a closed heading.
 *
 * The body is hidden, not unmounted (Polaris Collapsible unmounts a closed body), so edits
 * not yet saved and anything already loaded survive closing the section.
 */

/** Open state for one section: closed unless `attention`, and opened whenever it becomes so. */
export const useSectionOpen = (attention = false) => {
    const [open, setOpen] = useState(Boolean(attention));
    useEffect(() => {
        if (attention) setOpen(true);
    }, [attention]);
    const toggle = useCallback(() => setOpen((v) => !v), []);
    return [open, toggle];
};

/** The Show / Hide button for a section's heading. */
export const SectionToggle = ({ open, onToggle, controls }) => (
    <Button onClick={onToggle} ariaExpanded={open} ariaControls={controls} disclosure={open ? 'up' : 'down'}>
        {open ? 'Hide' : 'Show'}
    </Button>
);

/** A section's body: kept mounted, hidden while the section is closed. */
export const SectionBody = ({ open, id, children }) => (
    <div id={id} hidden={!open}>{children}</div>
);

/** One line for the Shipping Configuration heading. */
export const shippingSummary = ({ enabled, locationCount, fallbackFee }) => {
    if (!enabled) return 'Shipping rules are disabled for this brand.';
    const locations = locationCount === 0
        ? 'No 2W shipping locations ticked'
        : `2W shipping locations ticked: ${locationCount}`;
    const fee = String(fallbackFee ?? '').trim() === ''
        ? 'no fallback fee'
        : `fallback fee $${Number(fallbackFee).toFixed(2)}`;
    return `${locations} · ${fee}`;
};

/** One line for the Default Return Fee heading. */
export const returnFeeSummary = ({ hasDefault, defaultFee, isOverride, globalDefault, overrideCount, unavailable }) => {
    let fee;
    if (unavailable) fee = 'Return fees could not be loaded';
    else if (hasDefault && String(defaultFee ?? '').trim() !== '') {
        fee = `$${Number(defaultFee).toFixed(2)} on every order${isOverride ? ' (set for this brand)' : ''}`;
    } else if (globalDefault !== null && globalDefault !== undefined) {
        fee = `Global default, $${Number(globalDefault).toFixed(2)}`;
    } else fee = 'No default return fee set';
    if (unavailable || !overrideCount) return fee;
    return `${fee} · ${overrideCount} retailer override${overrideCount === 1 ? '' : 's'}`;
};
